import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, Search, Brain, CheckCircle, Zap, Sparkles, Globe } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/contexts/LanguageContext';
import ReasoningStep from './ReasoningStep';

/**
 * ReasoningPanel — collapsible "Agent Thinking" panel.
 *
 * Renders a vertical timeline of streaming agent steps. Designed to sit
 * inline with an assistant message bubble. Auto-scrolls to keep the
 * latest step visible while expanded and the agent is still streaming.
 *
 * Props
 *   steps       — Array<{ type: string, content: string, timestamp?: number, agent?: string, meta?: object }>
 *   isStreaming — boolean (true while events are still arriving)
 *   defaultOpen — boolean (open without user click, e.g. while streaming)
 *   onToggle    — (nextOpen: boolean) => void
 */
export const ReasoningPanel = ({ steps = [], isStreaming = false, defaultOpen = false, onToggle }) => {
  const { language } = useLanguage();
  const isArabic = language === 'ar';
  const [open, setOpen] = useState(defaultOpen);
  const scrollRef = useRef(null);

  // Jul 2026 redesign — Claude/ChatGPT activity-line idiom:
  //   • collapsed by default, even while streaming (no auto-open)
  //   • collapsed header shows the LATEST step's text with a shimmer
  //     while streaming, instead of a generic "Agent thinking… (N)"
  //   • consecutive duplicate steps ("Finalizing your review…" ×14)
  //     collapse into one entry with a ×N counter
  const dedupedSteps = [];
  for (const s of steps || []) {
    const prev = dedupedSteps[dedupedSteps.length - 1];
    if (prev && prev.content === s.content && prev.agent === s.agent) {
      prev._count = (prev._count || 1) + 1;
    } else {
      dedupedSteps.push({ ...s, _count: 1 });
    }
  }

  // Auto-scroll on new steps when expanded
  useEffect(() => {
    if (!open || !scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [dedupedSteps.length, open]);

  // Elapsed wall clock while streaming — mirrors the "Thinking… · 10s"
  // affordance. Starts on the first streamed event, stops when done.
  const startRef = useRef(null);
  const [elapsedS, setElapsedS] = useState(0);
  useEffect(() => {
    if (!isStreaming) return undefined;
    if (startRef.current == null) startRef.current = performance.now();
    const id = setInterval(
      () => setElapsedS(Math.floor((performance.now() - startRef.current) / 1000)),
      1000,
    );
    return () => clearInterval(id);
  }, [isStreaming]);

  if (!dedupedSteps.length && !isStreaming) return null;

  const handleToggle = () => {
    const next = !open;
    setOpen(next);
    onToggle?.(next);
  };

  // Jul 2026 product decision: reasoning and agent activity are ALWAYS
  // English, regardless of UI language — they read as technology.
  const latest = dedupedSteps[dedupedSteps.length - 1];
  const headerLabel = isStreaming
    ? ((latest && latest.content) || 'Thinking…')
    : 'How I reasoned';

  // While streaming: show a live mini-timeline (the last few steps with
  // a connector line) plus a shimmering "current activity · Ns" footer —
  // the Claude activity idiom. When done: collapse to one quiet line.
  if (isStreaming && !open) {
    const visible = dedupedSteps.slice(-4);
    return (
      <div className={cn('mt-2', isArabic && 'font-arabic')} data-testid="reasoning-panel">
        <style>{`
          @keyframes rpShimmer { from { background-position: 200% 0; } to { background-position: -200% 0; } }
          .rp-shimmer {
            background: linear-gradient(90deg, #9b9b95 35%, #1f1f1f 50%, #9b9b95 65%);
            background-size: 200% 100%;
            -webkit-background-clip: text;
            background-clip: text;
            color: transparent;
            animation: rpShimmer 2.2s linear infinite;
          }
        `}</style>
        <button
          type="button"
          onClick={handleToggle}
          aria-expanded={false}
          className="w-full text-start px-1"
          data-testid="reasoning-panel-toggle"
        >
          <ol className="space-y-0">
            {visible.map((step, idx) => (
              <li key={idx} className="flex items-stretch gap-2.5">
                <div className="flex flex-col items-center w-4 flex-shrink-0">
                  <span className="w-1.5 h-1.5 rounded-full mt-2" style={{ background: '#C9C6BD' }} />
                  <span className="flex-1 w-px" style={{ background: 'rgba(0,0,0,0.08)' }} />
                </div>
                <span className="text-[12.5px] text-[#8a887f] pb-2.5 truncate">
                  {step.content}
                  {step._count > 1 ? ` ×${step._count}` : ''}
                </span>
              </li>
            ))}
            <li className="flex items-center gap-2.5">
              <span className="w-4 flex justify-center flex-shrink-0">
                <span
                  aria-hidden
                  style={{
                    width: 13, height: 13, borderRadius: '50%',
                    border: '1.5px solid rgba(0,0,0,0.15)', borderTopColor: '#1a1a1a',
                    animation: 'rpSpin 0.8s linear infinite', flexShrink: 0, display: 'inline-block',
                  }}
                />
              </span>
              <style>{`@keyframes rpSpin { to { transform: rotate(360deg); } }`}</style>
              <span className="text-[13px] rp-shimmer" data-testid="reasoning-streaming-cursor">
                Thinking…
              </span>
              <span className="text-[11px] text-[#BBBBBB] tabular-nums">· {elapsedS}s</span>
            </li>
          </ol>
        </button>
      </div>
    );
  }

  return (
    <div
      className={cn('mt-2', isArabic && 'font-arabic')}
      data-testid="reasoning-panel"
    >
      <style>{`
        @keyframes rpShimmer {
          from { background-position: 200% 0; }
          to   { background-position: -200% 0; }
        }
        .rp-shimmer {
          background: linear-gradient(90deg, #9b9b95 35%, #1f1f1f 50%, #9b9b95 65%);
          background-size: 200% 100%;
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          animation: rpShimmer 2.2s linear infinite;
        }
      `}</style>
      <button
        type="button"
        onClick={handleToggle}
        aria-expanded={open}
        className="w-full flex items-center gap-2.5 px-1 py-1 text-start"
        data-testid="reasoning-panel-toggle"
      >
        <Sparkles
          className={cn('w-3.5 h-3.5 flex-shrink-0 text-[#7C5E3C]', isStreaming && 'animate-pulse')}
        />
        <span
          className={cn(
            'text-[13px] truncate',
            isStreaming ? 'rp-shimmer' : 'text-[#999999]',
          )}
        >
          {headerLabel}
        </span>
        {!isStreaming && dedupedSteps.length > 0 && (
          <span className="text-[11px] text-[#BBBBBB] flex-shrink-0">
            · {dedupedSteps.length}
          </span>
        )}
        <ChevronDown
          className={cn(
            'w-3.5 h-3.5 text-[#BBBBBB] flex-shrink-0 transition-transform',
            open && 'rotate-180',
          )}
        />
      </button>

      {open && (
        <div
          ref={scrollRef}
          className="max-h-64 overflow-y-auto ms-2 ps-3 py-1 border-s border-black/10"
          data-testid="reasoning-panel-list"
        >
          <ol className="space-y-2">
            {dedupedSteps.map((step, idx) => (
              <li key={idx} className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <ReasoningStep step={step} index={idx} isArabic={isArabic} />
                </div>
                {step._count > 1 && (
                  <span className="text-[10px] text-[#BBBBBB] tabular-nums flex-shrink-0 mt-0.5">
                    ×{step._count}
                  </span>
                )}
              </li>
            ))}
            {isStreaming && (
              <li className="flex items-center gap-2 text-xs text-[#999999]" data-testid="reasoning-streaming-cursor">
                <span className="w-1.5 h-1.5 rounded-full bg-[#7C5E3C] animate-pulse" />
                reasoning…
              </li>
            )}
          </ol>
        </div>
      )}
    </div>
  );
};

// ── ReasoningStep + pickIcon extracted to ReasoningStep.jsx for
// lint (`react-hooks/static-components`) compliance — see Cluster I.D.

export default ReasoningPanel;
